/**
 * Per-project 3D binary assets (GLB/GLTF/STL) in IndexedDB — local only.
 */

const DB_NAME = "prometheus_asset3d_v1";
const STORE = "assets";
const DB_VERSION = 1;
/** Soft cap — keep Artifact responsive */
export const MAX_ASSET_BYTES = 48 * 1024 * 1024;

export type Asset3dFormat = "glb" | "gltf" | "stl";

export type StoredAsset3d = {
  projectId: string;
  fileName: string;
  format: Asset3dFormat;
  mime: string;
  byteLength: number;
  blob: Blob;
  updatedAt: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error("IDB open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "projectId" });
      }
    };
  });
}

function formatFromName(name: string): Asset3dFormat | null {
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext === "glb") return "glb";
  if (ext === "gltf") return "gltf";
  if (ext === "stl") return "stl";
  return null;
}

export function detectAssetFormat(file: File): Asset3dFormat | null {
  return formatFromName(file.name);
}

export async function saveProjectAsset3d(
  projectId: string,
  file: File
): Promise<StoredAsset3d> {
  const format = detectAssetFormat(file);
  if (!format) {
    throw new Error("Formato no soportado. Usa .glb, .gltf o .stl");
  }
  if (file.size > MAX_ASSET_BYTES) {
    throw new Error(
      `Archivo demasiado grande (${Math.round(file.size / 1024 / 1024)} MB). Máx ~48 MB.`
    );
  }

  const record: StoredAsset3d = {
    projectId,
    fileName: file.name,
    format,
    mime: file.type || "application/octet-stream",
    byteLength: file.size,
    blob: file,
    updatedAt: new Date().toISOString(),
  };

  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("IDB write failed"));
    tx.objectStore(STORE).put(record);
  });
  db.close();
  window.dispatchEvent(
    new CustomEvent("prometheus-asset3d-changed", { detail: { projectId } })
  );
  return record;
}

export async function getProjectAsset3d(
  projectId: string
): Promise<StoredAsset3d | null> {
  const db = await openDb();
  const record = await new Promise<StoredAsset3d | null>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(projectId);
    req.onsuccess = () => resolve((req.result as StoredAsset3d) || null);
    req.onerror = () => reject(req.error || new Error("IDB read failed"));
  });
  db.close();
  return record;
}

export async function clearProjectAsset3d(projectId: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("IDB delete failed"));
    tx.objectStore(STORE).delete(projectId);
  });
  db.close();
  window.dispatchEvent(
    new CustomEvent("prometheus-asset3d-changed", { detail: { projectId } })
  );
}

export function objectUrlFromAsset(asset: StoredAsset3d): string {
  return URL.createObjectURL(asset.blob);
}
